import React, { FC, useEffect } from 'react';

import { FormikValues, useFormik } from 'formik';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';

import {
  ContractTypes,
  ICalendarEventType,
  ProfessionalReplacementTypes,
  RecurrenceActions,
  getRecurrence,
  pharmacyWorkshift,
  recurrenceType,
  updateWorkshift,
  updateWorkshiftContract,
} from '@pharmaplan/common';

import useGetChangedContract from '../../../../hooks/LongTermContract/useGetChangedContract';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import useUpdateWorkshift from '../../../../hooks/workshift/useUpdateWorkshift';
import CustomTextField from '../../../common/CustomTextField';
import { selectFooterPaginationCurrent } from '../../../../selectors/drawerSelector';
import { toJSDate } from '../../../Dashboard/MainCalendar/helpers';
import { makeReqParams } from '../../../../helpers/workshift/functions';
import genericClasses from '../../../../theme/GenericClasses';
import { InvertedRepeatMap, isIpad } from '../../../../helpers/Constants';
import WorkshiftForm from '../WorkshiftForm';
import strings from '../../../../localization';

import Validator from '../Validator';
import classes from './styles';
import useUpdateContract from '../../../../hooks/LongTermContract/useUpdateContract';
import {
  decimalConverterProxy,
  handlerForZeroes,
} from '../../../../helpers/proxyHandlers';
import useSyncMultiHours from '../../../../hooks/workshift/useSyncMultiHours';

interface IUpdate {
  eventIds: Array<string>;
  recurrenceId?: string;
  recType?: RecurrenceActions;
  type: ICalendarEventType;
}

const { Never } = recurrenceType;
const { pharmacist } = ProfessionalReplacementTypes;
const { series } = RecurrenceActions;

const Update: FC<IUpdate> = ({ eventIds, recurrenceId, recType, type }) => {
  const dispatch = useAppDispatch();
  const current = useAppSelector(selectFooterPaginationCurrent);
  const { setAttributes, formatDisplayDate } = useUpdateContract();

  const currentWorkshift = useAppSelector(pharmacyWorkshift);

  const { recurrence, notes, endDate, startDate, longTermShiftContract } = currentWorkshift ?? {};
  const {
    recurrenceType: currentRecurrenceType,
    endDate: recurrenceEndDate,
    startDate: recurrenceStartDate,
  } = recurrence ?? {};

  const {
    longTermShiftContractId,
    startDate: longContractStart,
    endDate: longContractEnd,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    allowanceNotes,
  } = longTermShiftContract ?? {};

  const valuesToParse = {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
  };

  const valuesHandledForZero = new Proxy(valuesToParse, handlerForZeroes);
  const valuesHandledForDecimals = new Proxy(
    valuesHandledForZero,
    decimalConverterProxy,
  );

  const isLongTerm = !!longTermShiftContractId;

  const { getChangedItems, comparator } = useGetChangedContract();

  const recurrenceSelector = useAppSelector(getRecurrence);
  const { repeatEvery } = recurrenceSelector ?? {};

  const id = eventIds[current - 1];
  const isSeries = recType === series;
  const start = isSeries && recurrenceStartDate ? recurrenceStartDate : startDate;

  const { container } = classes;
  const { borderNoneTextField } = genericClasses;

  const handleSubmit = (values: FormikValues) => {
    if (values.contractType === ContractTypes.longTerm) {
      const { startDate: defaultStart, endDate: defaultEnd } = comparator();

      dispatch(
        updateWorkshiftContract({
          ...makeReqParams(values, recurrenceSelector, {
            startDate: defaultStart,
            endDate: defaultEnd,
          }),
          workshiftId: id,
          shiftContractAttributes: getChangedItems(),
          typeReplacement: values?.replacementType,
          longTermContractId: longTermShiftContractId ?? '',
        }),
      );

      return;
    }

    // NOTE: THE ORDER MATTERS
    dispatch(
      updateWorkshift({
        ...makeReqParams(values, recurrenceSelector),
        recurrenceId: isSeries ? recurrenceId ?? '' : null,
        workshiftId: id,
        typeReplacement: values?.replacementType,
      }),
    );
  };

  const recurrenceTypeForWorkshift = isSeries
    ? currentRecurrenceType ?? Never
    : repeatEvery;

  let dates: Record<string, string | undefined> = {
    endDate: recurrenceEndDate || endDate,
  };

  if (isLongTerm) {
    dates = {
      endDate: longContractEnd,
      startDate: longContractStart,
    };
  }

  const formik = useFormik({
    initialValues: {
      ...currentWorkshift,
      ...valuesHandledForDecimals,
      selectedDate: DateTime.fromISO(start, {
        setZone: true,
      }),
      displayDate: formatDisplayDate(longContractStart, longContractEnd),
      allDay: false,
      ...dates,
      notes,
      allowanceNotes,
      startTime: toJSDate(startDate),
      endTime: toJSDate(endDate),
      contractType: isLongTerm
        ? ContractTypes.longTerm
        : ContractTypes.shortTerm,
      recurrence: InvertedRepeatMap[recurrenceTypeForWorkshift],
    },

    enableReinitialize: true,
    validationSchema: Validator(),
    onSubmit: handleSubmit,
  });

  useUpdateWorkshift(eventIds, formik, recurrenceId, recType, type);

  useEffect(() => {
    setAttributes(longContractStart, longContractEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longContractStart, longContractEnd]);

  useSyncMultiHours(formik);

  const notesSx = isIpad ? classes.notes : {};

  return (
    <Box sx={container}>
      <WorkshiftForm
        openedDate={startDate}
        formik={formik}
        isSeries={isSeries}
        isUpdate
      />

      <Box sx={notesSx}>
        <CustomTextField
          variant="outlined"
          formik={formik}
          id="notes"
          placeholder=" "
          label={strings.notes}
          name="notes"
          multiline
          maxLength={200}
          customClass={borderNoneTextField}
        />
      </Box>
    </Box>
  );
};

export default Update;
